import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTaskMutations } from "@/api/tasks/useTaskMutations"
import { useStatusScheme } from "@/api/statusSchemes/useStatusScheme"
import { useVisibleAttributeFields } from "@/api/metadata/useVisibleAttributeFields"
import { useAttributeDefinitions } from "@/api/metadata/useAttributeDefinitions"
import { useWorkspace } from "@/workspaces/WorkspaceContext"
import { useUser } from "@/auth/UserContext"
import { useProjects } from "@/api/projects/useProjects"
import { AttributeFieldChip } from "@/features/attributes/attribute-field-chip"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon, Cancel01Icon } from "@hugeicons/core-free-icons"
import { usePeople } from "@/api/people/usePeople"
import type { CellSelectOption } from "@/components/cell-controls/cell-controls"

const RichTextMarkdownEditor = lazy(() =>
  import("@/components/ui/rich-text-markdown-editor").then((module) => ({
    default: module.RichTextMarkdownEditor,
  })),
)

const TASK_FIELDS_RAIL_SURFACE = "task_fields_rail"

const PRIORITY_COLOR: Record<"low" | "medium" | "high", string> = {
  low: "#94a3b8",
  medium: "#f59e0b",
  high: "#f43f5e",
}

const PRIORITY_LABELS: Record<"low" | "medium" | "high", string> = {
  low: "Низкий",
  medium: "Средний",
  high: "Высокий",
}

type Priority = "low" | "medium" | "high"

export function CreateTaskDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  /** Called with the newly-created task id after successful creation. */
  onSuccess?: (taskId: string) => void
}) {
  const navigate = useNavigate()
  const { path, workspace } = useWorkspace()
  const { user } = useUser()
  const { createTask } = useTaskMutations()
  const { statuses: taskStatuses } = useStatusScheme("task")
  const { peopleByUserId, people } = usePeople()

  const { fields: visibleFields } = useVisibleAttributeFields(
    "task",
    TASK_FIELDS_RAIL_SURFACE,
  )

  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [customAttributes, setCustomAttributes] = useState<Record<string, unknown>>({})
  const [error, setError] = useState<string | null>(null)
  const titleRef = useRef<HTMLInputElement>(null)

  const currentPerson = user ? peopleByUserId.get(user.id) : null

  const { projects } = useProjects()
  const { data: taskAttributes = [] } = useAttributeDefinitions("task")

  useEffect(() => {
    if (!open) return
    const defaults: Record<string, unknown> = {}
    if (currentPerson) {
      defaults.assignee_id = currentPerson.id
    }
    const firstStatus = taskStatuses.find((s) => s.category === "todo") ?? taskStatuses[0]
    if (firstStatus) {
      defaults.status = firstStatus.slug
    }
    defaults.priority = "low"
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCustomAttributes(defaults)
  }, [open])

  const priorityOptions = useMemo<CellSelectOption[]>(
    () =>
      (["low", "medium", "high"] as const).map((p) => ({
        value: p,
        label: PRIORITY_LABELS[p],
        color: PRIORITY_COLOR[p],
      })),
    [],
  )

  const slugOptions = useMemo<Record<string, CellSelectOption[]>>(() => {
    const out: Record<string, CellSelectOption[]> = {}
    if (visibleFields.some((f) => f.slug === "priority")) {
      out.priority = priorityOptions
    }
    if (visibleFields.some((f) => f.slug === "status")) {
      out.status = taskStatuses.map((s) => ({
        value: s.slug,
        label: s.label,
        color: s.color,
      }))
    }
    if (visibleFields.some((f) => f.slug === "assignee_id")) {
      out.assignee_id = people
        .filter((p) => p.id)
        .map((p) => ({ value: p.id, label: p.name }))
    }
    if (taskAttributes.some((a) => a.slug === "project_id")) {
      out.project_id = projects.map((p) => ({ value: p.id, label: p.name }))
    }
    return out
  }, [visibleFields, priorityOptions, taskStatuses, people, projects, taskAttributes])

  const projectAttrDef = useMemo(
    () => taskAttributes.find((a) => a.slug === "project_id"),
    [taskAttributes],
  )

  const allFieldDefinitions = useMemo(
    () =>
      projectAttrDef && !visibleFields.some((f) => f.slug === "project_id")
        ? [...visibleFields, projectAttrDef]
        : visibleFields,
    [visibleFields, projectAttrDef],
  )

  function handleCustomAttributeChange(slug: string, value: unknown) {
    setCustomAttributes((current) => {
      const next = { ...current }
      if (value === null || value === undefined || value === "") {
        delete next[slug]
      } else {
        next[slug] = value
      }
      return next
    })
  }

  function resetState() {
    setTitle("")
    setDescription("")
    setCustomAttributes({})
    setError(null)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    setError(null)

    const trimmedTitle = title.trim()
    const mergedCustom: Record<string, unknown> = { ...customAttributes }

    const payload: {
      title: string
      description?: string
      assigneeId?: string | null
      projectId?: string | null
      creatorId?: string | null
      priority?: Priority
      customAttributes?: Record<string, unknown>
    } = { title: trimmedTitle }

    if (description.trim()) {
      payload.description = description.trim()
    }

    if ("assignee_id" in mergedCustom) {
      payload.assigneeId = (mergedCustom.assignee_id as string) || null
      delete mergedCustom.assignee_id
    }
    if ("project_id" in mergedCustom) {
      payload.projectId = (mergedCustom.project_id as string) || null
      delete mergedCustom.project_id
    }
    if ("priority" in mergedCustom) {
      const raw = mergedCustom.priority
      if (raw === "low" || raw === "medium" || raw === "high") {
        payload.priority = raw
      }
      delete mergedCustom.priority
    }
    if (currentPerson) {
      payload.creatorId = currentPerson.id
    }

    if (Object.keys(mergedCustom).length > 0) {
      payload.customAttributes = mergedCustom
    }

    createTask.mutate(
      payload,
      {
        onSuccess: (task) => {
          resetState()
          onClose()
          if (onSuccess) {
            onSuccess(task.id)
            return
          }
          navigate(path(`/tasks/${task.id}`))
        },
        onError: (e) => setError(e instanceof Error ? e.message : "Ошибка создания"),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent
        className="!w-[94vw] !max-w-none sm:!w-[600px] sm:!max-w-[600px] gap-0 overflow-hidden p-0"
        overlayClassName="bg-black/30"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Новая задача</DialogTitle>

        <div className="flex items-center gap-2 border-b border-border/70 px-4 py-2.5 text-[13px]">
          <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-foreground/10 text-[11px] font-semibold text-foreground/80">
            {workspace.name?.slice(0, 2).toUpperCase() ?? "WS"}
          </div>
          <span className="truncate font-medium text-foreground/80">{workspace.name}</span>
          <HugeiconsIcon
            icon={ArrowRight01Icon}
            strokeWidth={2}
            className="size-3.5 shrink-0 text-muted-foreground/60"
          />
          <span className="text-muted-foreground">Новая задача</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="ml-auto"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex max-h-[80vh] flex-col">
          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-5">
            <Input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Название задачи…"
              autoFocus
              className="h-11 border-0 bg-transparent px-0 text-2xl leading-tight font-bold shadow-none ring-0 placeholder:text-muted-foreground/60 focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />

            {allFieldDefinitions.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {allFieldDefinitions.map((definition) => (
                  <AttributeFieldChip
                    key={definition.id}
                    definition={definition}
                    value={customAttributes[definition.slug] ?? null}
                    onCommit={(next) => handleCustomAttributeChange(definition.slug, next)}
                    statusOptions={definition.slug === "status" ? taskStatuses : undefined}
                    slugOptions={slugOptions}
                    placeholder={definition.name}
                  />
                ))}
              </div>
            ) : null}

            <div className="mt-5 border-t border-border/60 pt-4">
              <Suspense
                fallback={
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-20 rounded" />
                    <Skeleton className="h-[120px] w-full rounded-xl" />
                  </div>
                }
              >
                <RichTextMarkdownEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Описание задачи…"
                  minHeight={120}
                  className="border-0 shadow-none"
                />
              </Suspense>
            </div>

            {error ? (
              <p className="mt-3 text-xs text-destructive">{error}</p>
            ) : null}
          </div>

          <DialogFooter className="px-6">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || createTask.isPending}
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              {createTask.isPending ? "Создание…" : "Создать задачу"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
